import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type RouteMeta
} from 'vue-router'
import { createNabooAdminRoutes } from '@/presentation/router/routes.ts'
import { useAuthStore } from '@/presentation/stores/auth'
import { useQueryCacheStore } from '@/presentation/stores/router/queryCache'
import { storeToRefs } from 'pinia'

export interface CustomRouteMeta extends RouteMeta {
  assertQueries: (route: RouteLocationNormalized) => boolean
}

const nabooAdminRouter = () => {
  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: createNabooAdminRoutes()
  })

  router.beforeEach((to, from, next) => {
    // Gestion du cache des query parameters avec TTL et cleanup automatique
    const queryCache = useQueryCacheStore()

    if (to.name === from.name) {
      // Sauvegarder les query params si présents
      if (Object.keys(from.query).length > 0) {
        queryCache.set(to.path, from.query)
      }

      // Restaurer les query params si absents mais en cache
      if (Object.keys(to.query).length === 0) {
        const cachedQuery = queryCache.get(to.path)
        if (cachedQuery) {
          next({ path: to.path, query: cachedQuery })
          return
        }
      }
    }

    // Enforce roles from route meta if present
    const authStore = useAuthStore()
    const { isAuthenticated, getRoles } = storeToRefs(authStore)
    const requiredRoles = (to.meta && (to.meta as any).roles) as string[] | undefined

    if (requiredRoles && requiredRoles.length > 0) {
      // If not authenticated, let authGuard on routes handle redirect/login
      if (!isAuthenticated.value) {
        next()
        return
      }

      const hasAccess = getRoles.value?.some((r: string) => requiredRoles.includes(r))
      if (!hasAccess) {
        next({ name: 'dashboard' })
        return
      }
    }
    next()
  })

  router.afterEach((to) => {
    if (to?.matched?.length > 0 && 'menuTitle' in to.matched[0].meta) {
      document.title = `Naboo - BO :: ${to.matched[0].meta.menuTitle}`
    } else {
      document.title = 'Naboo - BO'
    }
  })

  return router
}

export default nabooAdminRouter
